import React, { useState, useMemo } from "react";
import "./styles.scss";
import { ConfirmationPopup, DocumentUploadCard } from "..";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RedExclamationCircle } from "../../assets/svgs";
import { buildLocalFileKey, parseServerNameMeta } from "../../utils/document";
import useApp from "../../hooks/useApp";

const Tabbar = ({
  tabData,
  activeTabIndex,
  onClickTab,
  tabHeading,
  handleFileUpload,
  onAllRequiredUploaded,
  fileUploadStatus = {},
  documentList = [],
  onDeleteDocument,
  isLoadingDocuments = false,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { pathname } = location;
  const routeName = pathname.split("/")[1];
  const { handleResetFile } = useApp();

  const [showPopup, setShowPopup] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState(null);

  const currentTabGroupKey = useMemo(
    () => `tab-${activeTabIndex}`,
    [activeTabIndex]
  );

  // ✅ get docs for current tab (based on name containing "__g_tab-X__")
  const getCurrentTabDocuments = () => {
    const marker = `__g_${currentTabGroupKey}__`; // "__g_tab-0__"
    return (documentList || []).filter((doc) =>
      String(doc?.name || "").includes(marker)
    );
  };

  const handleTabClick = (nextIndex) => {
    if (nextIndex === activeTabIndex) return;

    // if current tab has uploaded docs, ask confirmation first
    const currentDocs = getCurrentTabDocuments();

    if (currentDocs.length > 0) {
      setPendingTabIndex(nextIndex);
      setShowPopup(true);
      return;
    }

    // no docs => normal switch
    onClickTab(nextIndex);
  };

  const handleOnClickCancel = () => {
    setShowPopup(false);
    setPendingTabIndex(null);
  };


  const handleOnClickConfirm = async () => {
    try {
      const docsToDelete = getCurrentTabDocuments();

      // Reset local state keys based on server meta + doc type
      // collect keys first (so even if delete fails mid-way, you can decide behavior)
      const keysToReset = docsToDelete
        .map((doc) => {
          const meta = parseServerNameMeta(doc?.name || "");
          return buildLocalFileKey({
            groupKey: meta.groupKey,
            documentType: doc.document_type,
            slotIndex: meta.slotIndex,
          });
        })
        .filter(Boolean);

      // delete all docs of current tab
      for (const doc of docsToDelete) {
        await onDeleteDocument?.(doc.id);
      }
      // reset local upload UI state for those keys
      keysToReset.forEach((k) => handleResetFile?.(k));

      // mark required docs for this tab as NOT uploaded anymore
      if (onAllRequiredUploaded) {
        onAllRequiredUploaded(currentTabGroupKey, false);
      }

      setShowPopup(false);

      const next = pendingTabIndex;
      setPendingTabIndex(null);

      if (typeof next === "number") {
        onClickTab(next);
      }
    } catch (e) {
      setShowPopup(false);
      setPendingTabIndex(null);
    }
  };

  const handleTabData = (tabItem, handleFileUpload, onAllRequiredUploaded) => {
    switch (tabItem?.tabType) {
      case "document":
        return handleTabDocument({
          tabData: tabItem?.tabData,
          documentCount: tabItem?.documentCount,
          handleFileUpload,
          onAllRequiredUploaded,
          fileUploadStatus,
          documentList,
          onDeleteDocument,
          isLoadingDocuments,
          activeTabIndex,
        });
      default:
        return null;
    }
  };

  const handleTabDocument = ({
    tabData,
    documentCount,
    handleFileUpload,
    onAllRequiredUploaded,
    fileUploadStatus,
    documentList,
    onDeleteDocument,
    isLoadingDocuments,
    activeTabIndex,
  }) => {
    const count = Number(documentCount);
    const documents = Array.from({ length: count }).map(() => ({
      ...tabData[0],
      type: `${tabData[0].type}`,
      label: `${tabData[0].label}`,
    }));

    return (
      <DocumentUploadCard
        documents={documents}
        handleFileUpload={handleFileUpload}
        onAllRequiredUploaded={onAllRequiredUploaded}
        fileUploadStatus={fileUploadStatus}
        fileKey={`tab-${activeTabIndex}`}
        groupKey={`tab-${activeTabIndex}`}
        documentList={documentList}
        onDeleteDocument={onDeleteDocument}
        isLoadingDocuments={isLoadingDocuments}
      />
    );
  };

  return (
    <div className="tab-container">
      <h2 className="tab-heading">{t(`${routeName}.${tabHeading}`)}</h2>

      <div className="tab-bar-container">
        {tabData?.map((item, index) => (
          <div
            key={index}
            className={`tab-item ${activeTabIndex === index && "tab-item-active"}`}
            style={{
              backgroundColor:
                activeTabIndex === index ? item.color : "var(--bs-neutral-50)",
            }}
            onClick={() => handleTabClick(index)} // use handler
          >
            <span>{t(`${routeName}.${item.tabLabel}`)}</span>
          </div>
        ))}
      </div>

      <div className="tab-content">
        {tabData[activeTabIndex] &&
          handleTabData(
            tabData[activeTabIndex],
            handleFileUpload,
            onAllRequiredUploaded
          )}
      </div>

      <ConfirmationPopup
        show={showPopup}
        icon={RedExclamationCircle}
        heading="remove_tab_popup.heading"
        description="remove_tab_popup.description"
        cancelButtonText="global.cancel"
        onClickCancel={handleOnClickCancel}
        confirmButtonText="global.confirm"
        onClickConfirm={handleOnClickConfirm}
      />
    </div>
  );
};

export default Tabbar;
